import dot from 'dot-prop-immutable';
import { assocPath } from 'ramda';
import { combineReducers, type Reducer } from 'redux';

import type { ActionWithPayload } from 'store/types';

type EntityRecord = Record<string, unknown>;
export type EntitiesState = Record<string, EntityRecord>;

// State here is always already-initialized (never undefined), unlike a
// top-level redux Reducer<S, A> which must also handle the S|undefined case.
export type EntitiesReducer<TState, Action> = (
  state: TState,
  action: Action,
) => TState;

type EntitiesSource = {
  entities: Record<string, EntityRecord>;
};

const isEntitiesSource = (obj: unknown, name: string): obj is EntitiesSource =>
  !!obj &&
  typeof obj === 'object' &&
  Object.prototype.hasOwnProperty.call(obj, 'entities') &&
  Object.prototype.hasOwnProperty.call((obj as EntitiesSource).entities, name);

const entityById =
  <Action extends ActionWithPayload<string, unknown>>(name: string) =>
  (state: EntityRecord | undefined, action: Action): EntityRecord => {
    const currentState = state ?? {};
    const { meta, payload } = action;
    const source = isEntitiesSource(meta, name)
      ? meta
      : isEntitiesSource(payload, name)
      ? payload
      : null;

    return source
      ? { ...currentState, ...source.entities[name] }
      : currentState;
  };

export const createEntitiesReducer = <
  TState extends { entities: Entities },
  Entities extends EntitiesState = TState['entities'],
  Action extends ActionWithPayload<string, unknown> = ActionWithPayload<
    string,
    unknown
  >,
>(
  names: (keyof Entities & string)[],
  actionTypes: Action['type'][],
): EntitiesReducer<TState, Action> => {
  const entityReducerByName = names.reduce<
    Record<string, Reducer<EntityRecord, Action>>
  >((acc, name) => {
    acc[name] = entityById<Action>(name);
    return acc;
  }, {});
  const reducer = combineReducers(entityReducerByName);

  return (state: TState, action: Action): TState => {
    let nextState = state;
    if (actionTypes.indexOf(action.type) !== -1) {
      nextState = {
        ...state,
        entities: reducer(state.entities, action) as Entities,
      };
    }
    for (const name of names) {
      if (!(name in nextState.entities)) {
        nextState = assocPath(['entities', name], {}, nextState);
      }
    }
    return nextState;
  };
};

export const createRehydrateReducer =
  <TState>(
    getScope: (state: Record<string, unknown>) => TState,
    paths: string[],
  ) =>
  (state: TState, action: { key: string; payload: unknown }): TState => {
    let scope: TState;
    try {
      scope = getScope({ [action.key]: action.payload });
    } catch {
      return state;
    }
    let nextState = { ...state };
    for (const path of paths) {
      const defaultValue = dot.get<TState, unknown>(state, path);
      const value = dot.get<TState, unknown>(scope, path, defaultValue);
      nextState = dot.set<TState, TState>(nextState, path, value);
    }
    return nextState;
  };
